/**
 * mapDataCache.js
 *
 * IndexedDB-backed cache for region business data used by map pages.
 *
 * Schema
 * ------
 * DB name : orangelabel-map-cache
 * Store   : regions
 * Key     : region:<slug>:v1:businesses   (e.g. "region:oceania:v1:businesses")
 * Value   : { fetched_at: <ISO string>, count: N, data: [...] }
 * TTL     : 6 hours
 *
 * Fallback
 * --------
 * If IndexedDB is unavailable (incognito / unsupported browser), an in-memory
 * Map is used instead. The in-memory cache lasts only for the current tab.
 *
 * Prefetch
 * --------
 * call startBackgroundPrefetch(currentSlug) once initial map data is loaded.
 * It fetches the other 4 region slugs in order (adjacent region first) with
 * 1.5 s gaps. Slow connections (2G) are skipped entirely.
 */

const DB_NAME = 'orangelabel-map-cache';
const STORE_NAME = 'regions';
const DB_VERSION = 1;
const TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

// In-memory fallback (lives for the tab session).
const _memCache = new Map();

// --------------------------------------------------------------------------
// IndexedDB helpers
// --------------------------------------------------------------------------

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      req.onsuccess = (e) => resolve(e.target.result);
      req.onerror = () => {
        _dbPromise = null; // allow retry
        reject(req.error);
      };
    } catch (err) {
      _dbPromise = null;
      reject(err);
    }
  });
  return _dbPromise;
}

async function idbGet(key) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return undefined;
  }
}

async function idbSet(key, value) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const req = tx.objectStore(STORE_NAME).put(value, key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // Ignore — worst case we re-fetch next time.
  }
}

// --------------------------------------------------------------------------
// Cache key helpers
// --------------------------------------------------------------------------

function cacheKey(slug) {
  return `region:${slug}:v1:businesses`;
}

function isExpired(entry) {
  if (!entry?.fetched_at) return true;
  return Date.now() - new Date(entry.fetched_at).getTime() > TTL_MS;
}

// --------------------------------------------------------------------------
// Network fetch
// --------------------------------------------------------------------------

// Paginated fetch across the whole region. A single limit=50000 used to
// work before the 2026-04-17 API hardening capped `limit` at 10000
// (backend/routes/businesses.py). Rather than bump that cap and open a
// DoS vector, we stream pages until `next_offset` is null.
async function fetchRegionFromNetwork(slug) {
  const pageSize = 5000;
  let offset = 0;
  const items = [];
  let safety = 0;
  while (safety++ < 50) {
    const url = `/api/businesses?region_slug=${encodeURIComponent(slug)}&paginated=true&limit=${pageSize}&offset=${offset}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status} for region ${slug}`);
    const payload = await res.json();
    const batch = Array.isArray(payload) ? payload : (payload.items || []);
    items.push(...batch);
    const nextOffset = payload && typeof payload === 'object' ? payload.next_offset : null;
    if (nextOffset == null) break;
    offset = nextOffset;
  }
  return items;
}

// --------------------------------------------------------------------------
// Public API
// --------------------------------------------------------------------------

/**
 * Returns businesses for a region slug.
 * Reads IDB (or memory fallback) first; falls back to network.
 * Always returns an array (never throws to caller).
 */
export async function getRegionBusinesses(slug) {
  const key = cacheKey(slug);

  // 1. Try IndexedDB
  try {
    const entry = await idbGet(key);
    if (entry && !isExpired(entry)) {
      return entry.data;
    }
  } catch {
    // IDB unavailable — try memory
  }

  // 2. Try in-memory fallback
  const memEntry = _memCache.get(key);
  if (memEntry && !isExpired(memEntry)) {
    return memEntry.data;
  }

  // 3. Fetch from network
  try {
    const data = await fetchRegionFromNetwork(slug);
    const entry = { fetched_at: new Date().toISOString(), count: data.length, data };
    // Persist
    try {
      await idbSet(key, entry);
    } catch (err) {
      // IDB write failed — most commonly QuotaExceededError on Safari (~50MB
      // limit). Fall back to in-memory cache and surface one warning so the
      // silent-fail-then-refetch cycle is traceable in dev tools.
      if (err && err.name === 'QuotaExceededError' && !_quotaWarned) {
        _quotaWarned = true;
        console.warn('[mapDataCache] IndexedDB quota exceeded — falling back to in-memory cache for this tab.');
      }
      _memCache.set(key, entry);
    }
    return data;
  } catch (err) {
    console.warn(`[mapDataCache] getRegionBusinesses(${slug}) network failed:`, err);
    return [];
  }
}

let _quotaWarned = false;

// --------------------------------------------------------------------------
// Region adjacency — defines prefetch order from a given slug
// --------------------------------------------------------------------------

const REGION_SLUGS = ['oceania', 'asia', 'americas', 'europe', 'africa'];

// Adjacent region order: for each slug, what order to prefetch the other 4?
const ADJACENCY_ORDER = {
  oceania:  ['asia', 'americas', 'europe', 'africa'],
  asia:     ['oceania', 'europe', 'africa', 'americas'],
  europe:   ['africa', 'asia', 'americas', 'oceania'],
  africa:   ['europe', 'asia', 'americas', 'oceania'],
  americas: ['europe', 'africa', 'asia', 'oceania'],
};

/**
 * Start background prefetch for all regions other than currentSlug.
 * - Runs in order of adjacency (adjacent first).
 * - 1.5 s gap between fetches.
 * - Skips if on a slow connection (2G).
 * - Strictly best-effort — never throws user-facing errors.
 * - Already-fresh cached entries are skipped.
 */
export function startBackgroundPrefetch(currentSlug) {
  // Skip on 2G
  try {
    if (navigator.connection?.effectiveType === '2g') return;
  } catch {}

  const order = ADJACENCY_ORDER[currentSlug] || REGION_SLUGS.filter(s => s !== currentSlug);

  (async () => {
    for (const slug of order) {
      try {
        const key = cacheKey(slug);

        // Check if already fresh in IDB
        let alreadyCached = false;
        try {
          const entry = await idbGet(key);
          if (entry && !isExpired(entry)) {
            alreadyCached = true;
          }
        } catch {}

        // Check memory fallback
        if (!alreadyCached) {
          const memEntry = _memCache.get(key);
          if (memEntry && !isExpired(memEntry)) {
            alreadyCached = true;
          }
        }

        if (!alreadyCached) {
          // Re-check slow connection before each fetch
          try {
            if (navigator.connection?.effectiveType === '2g') break;
          } catch {}

          const data = await fetchRegionFromNetwork(slug);
          const entry = { fetched_at: new Date().toISOString(), count: data.length, data };
          try {
            await idbSet(key, entry);
          } catch {
            _memCache.set(key, entry);
          }
        }

        // Space fetches 1.5 s apart
        await new Promise(r => setTimeout(r, 1500));
      } catch {
        // Swallow all errors — prefetch is best-effort
      }
    }
  })();
}
